import { dateOnly } from '../../domain/date-only.ts';
import type { WorkWeek } from '../../domain/entities/company.ts';
import type { WorkdayException } from '../../domain/entities/schedule.ts';
import { workCalendar, type WorkCalendar } from '../../domain/work-calendar.ts';

/**
 * Assemble the calendar a job's dates are computed against.
 *
 * Everything that moves a date — creating an item, dragging a Gantt bar,
 * cascading a predecessor, comparing against baseline — resolves through one
 * of these, so building it is deliberately strict. A calendar quietly carrying
 * another job's holidays would shift dates with no visible cause, and the
 * resulting argument about dates would land weeks later.
 */
export function jobCalendar(
  workWeek: WorkWeek,
  exceptions: readonly WorkdayException[],
  jobId?: string,
): WorkCalendar {
  const byDate: Record<string, WorkdayException['kind']> = {};

  for (const exception of exceptions) {
    if (jobId !== undefined && exception.jobId !== jobId) {
      throw new TypeError(
        `Workday exception for ${exception.date} belongs to job ${exception.jobId}, not ${jobId}`,
      );
    }
    // Throws on anything that is not YYYY-MM-DD, rather than letting a
    // malformed key sit in the map where it would never match a lookup.
    const date = dateOnly(exception.date);

    const existing = byDate[date];
    if (existing !== undefined && existing !== exception.kind) {
      throw new TypeError(
        `Two conflicting workday exceptions for ${date}: ${existing} and ${exception.kind}`,
      );
    }
    byDate[date] = exception.kind;
  }

  return workCalendar(workWeek, byDate);
}
